import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CapabilitiesResponseDataSchema,
  CapabilityCampaignAssignation,
  CapabilityIdentifier,
  CapabilityResponseDataSchema,
  ContactChannel,
  SupportCapabilityConfigSchema,
  SupportEscalationStrategy,
} from "schema"
import { toast } from "sonner"
import z from "zod"
import { useInstallCapabilityMutation } from "../../hooks/useInstallCapabilityMutation"
import { useUpdateCapabilityConfigMutation } from "../../hooks/useUpdateCapabilityConfigMutation"
import { SupportFields } from "./SupportFields"

type SupportConfig = z.infer<typeof SupportCapabilityConfigSchema>

type Props = {
  capability: z.infer<typeof CapabilitiesResponseDataSchema>[number]
  mode?: "install" | "update"
  initialConfig?: SupportConfig
  onClose: () => void
  onPendingChange?: (isPending: boolean) => void
  onConfigure?: (capability: z.infer<typeof CapabilityResponseDataSchema>) => void
  onSubmitHandlerRef?: React.MutableRefObject<
    | ((
        campaignAssignation: CapabilityCampaignAssignation,
        campaignIds: string[],
        campaignFlow?: "Inbound" | "Outbound",
      ) => Promise<void>)
    | null
  >
}

export function SupportForm({
  capability,
  mode = "install",
  initialConfig,
  onClose,
  onPendingChange,
  onConfigure,
  onSubmitHandlerRef,
}: Props) {
  const { t } = useTranslation()
  const [config, setConfig] = useState<SupportConfig>({
    handoffPolicy: initialConfig?.handoffPolicy ?? "DeflectGracefully",
    escalationStrategy:
      initialConfig?.escalationStrategy ?? SupportEscalationStrategy.OneTicketPerIssue,
    supportPhone: initialConfig?.supportPhone ?? "",
    supportEmail: initialConfig?.supportEmail ?? "",
    replyPolicyByChannel: initialConfig?.replyPolicyByChannel ?? {},
    // Default a stored config that predates these fields to the previous behavior
    // (ask for phone or email, no extra fields).
    contactChannel: initialConfig?.contactChannel ?? ContactChannel.PhoneOrEmail,
    additionalFields: initialConfig?.additionalFields ?? [],
  })

  const { mutateAsync: installCapability, isPending: isInstallPending } =
    useInstallCapabilityMutation()
  const { mutateAsync: updateCapabilityConfig, isPending: isUpdatePending } =
    useUpdateCapabilityConfigMutation()

  const isPending = mode === "install" ? isInstallPending : isUpdatePending

  useEffect(() => {
    onPendingChange?.(isPending)
  }, [isPending, onPendingChange])

  const handleSubmit = useCallback(
    async (
      campaignAssignation: CapabilityCampaignAssignation,
      campaignIds: string[],
      campaignFlow?: "Inbound" | "Outbound",
    ) => {
      if (isPending) return

      if (!SupportCapabilityConfigSchema.safeParse(config).success) {
        toast.error(t("capabilities.catalog.Support.config.replyPolicy.invalidConfig"))
        return
      }

      let installedCapability: z.infer<typeof CapabilityResponseDataSchema> | undefined

      if (mode === "install") {
        installedCapability = await installCapability({
          identifier: CapabilityIdentifier.Support,
          config,
          campaignAssignation,
          campaignIds,
          campaignFlow,
        })

        toast.success(
          t("capabilities.list.install.success", {
            capability: t("capabilities.catalog.Support.name"),
          }),
        )
      } else {
        await updateCapabilityConfig({ id: capability.id, config })
      }

      if (onConfigure && installedCapability) {
        onConfigure(installedCapability)
      } else {
        onClose()
      }
    },
    [
      isPending,
      mode,
      config,
      capability.id,
      installCapability,
      updateCapabilityConfig,
      onConfigure,
      onClose,
      t,
    ],
  )

  if (onSubmitHandlerRef) {
    onSubmitHandlerRef.current = handleSubmit
  }

  return <SupportFields value={config} onChange={setConfig} />
}
